import type {DominioTiempo} from "services-comun-meteored/modules/portal/tiempo";
import type {
    IDataV4,
    IRespuestaNewsLast,
    IRespuestaV4DataRedaccionNewsWeb as INoticia,
    TListadoNewsLast,
} from "services-comun-meteored/modules/services/interface";
import type {Langs} from "i18n/langs";
import type {Respuesta} from "services-comun/modules/net/request/respuesta";
import network from "services-comun-tiempo/modules/portal/config/network";
import traduccionCalendarioLoader from "i18n/calendario";
import traduccionNoticiasLoader from "i18n/noticias";

import {ERedaccionTipo} from "../auxiliares";
import {Noticia, type Traduccion} from "..";

export interface ICache {
    data: Noticia[];
    expiracion: number;
    expires: string;
}

export type DataSource = (dominio: DominioTiempo, idioma: string, tipo: number)=>Promise<Respuesta<IDataV4<TListadoNewsLast, IRespuestaNewsLast>>>;

export abstract class Builder {
    private readonly cache: Record<string, ICache|undefined>;

    protected constructor(private readonly ds: DataSource, protected tipo: ERedaccionTipo) {
        this.cache = {};
    }

    public async load(dominio: DominioTiempo): Promise<ICache> {
        const now = Date.now();

        const actual = this.cache[dominio.id];
        if (actual != undefined && actual.expiracion < now) {
            return actual;
        }

        const [calendario, noticias] = await Promise.all([
            traduccionCalendarioLoader(dominio.idioma as Langs),
            traduccionNoticiasLoader(dominio.idioma as Langs),
        ]);
        const traduccion: Traduccion = {
            calendario,
            noticias,
        };

        const salida = await this.ds(dominio, dominio.idioma, this.tipo)
            .then((data)=>{
                const expiracion = data.expires??new Date(now+600000);
                const news = <INoticia[]>data.data.respuesta.news??[];
                const configuracion = network.getDominio(dominio);
                const salida: ICache = {
                    expiracion: expiracion.getTime(),
                    expires: expiracion.toISOString(),
                    data: news.map((noticia)=>new Noticia(configuracion, noticia, traduccion)),
                };
                for (const noticia of data.data.respuesta.news as INoticia[]) {
                    salida.data.push(new Noticia(configuracion, noticia, traduccion));
                }

                return salida;
            });

        this.cache[dominio.id] = salida;

        if (salida.expiracion < now) {
            this.recalcularExpiracion(now, salida);
        }

        return salida;
    }

    private recalcularExpiracion(now: number, salida: ICache): void {
        const diferencia = now - salida.expiracion;
        if (diferencia < 1800000) { // menos de 30 minutos => 10 minutos más de caché
            salida.expiracion = now + 600000;
        } else if (diferencia < 3600000) { // menos de 1 hora => 1 hora más de caché
            salida.expiracion = now + 3600000;
        } else { // más de 1 hora => 1 día más de caché
            salida.expiracion = now + 86400000;
        }
        salida.expires = new Date(salida.expiracion).toISOString();
    }
}
